import { Injectable, inject, signal, computed } from '@angular/core';
import { SelfProfileService } from '../services/self-profile.service';
import { SelfProfile } from '../models/self-profile.model';

@Injectable({
  providedIn: 'root',
})
export class ProfileFacade {
  private readonly selfProfileService = inject(SelfProfileService);

  // ── State (private writable signals) ──────────────────────────────────────
  private readonly _profile = signal<SelfProfile | null>(this.loadStoredProfile());
  private readonly _isLoading = signal<boolean>(false);

  // ── Public read-only selectors ─────────────────────────────────────────────
  readonly profile = this._profile.asReadonly();
  readonly isLoading = this._isLoading.asReadonly();

  readonly userId = computed<number | null>(() => this._profile()?.idUser ?? null);
  readonly studentCode = computed<string | null>(() => this._profile()?.student?.studentCode ?? null);

  readonly fullName = computed<string>(() => {
    const p = this._profile();
    if (!p) return 'Usuario';
    return `${p.firstName || ''} ${p.lastName || ''}`.trim() || 'Usuario';
  });

  readonly userInitials = computed<string>(() => {
    const p = this._profile();
    if (!p || !p.firstName) return 'U';
    const firstInitial = p.firstName.charAt(0);
    const lastInitial = p.lastName ? p.lastName.charAt(0) : '';
    return `${firstInitial}${lastInitial}`.toUpperCase();
  });

  readonly avatarUrl = computed<string | null>(() => this._profile()?.avatarUrl ?? null);

  // ── Actions ────────────────────────────────────────────────────────────────
  fetchProfile(idUser: number): void {
    this._isLoading.set(true);
    this.selfProfileService.getProfile(idUser).subscribe({
      next: (res) => {
        this._isLoading.set(false);
        if (res.state && res.data) {
          this.setProfile(res.data);
        }
      },
      error: (err) => {
        this._isLoading.set(false);
        console.error('[ProfileFacade] Error fetching user profile:', err);
      },
    });
  }

  setProfile(profile: SelfProfile | null): void {
    this._profile.set(profile);
    try {
      if (profile) {
        localStorage.setItem('user_profile', JSON.stringify(profile));
      } else {
        localStorage.removeItem('user_profile');
      }
    } catch (e) {
      console.error('[ProfileFacade] Error persisting profile to localStorage:', e);
    }
  }

  clearProfile(): void {
    this.setProfile(null);
  }

  private loadStoredProfile(): SelfProfile | null {
    const stored = localStorage.getItem('user_profile');
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch {
      return null;
    }
  }
}
